/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useEffect, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EuiButtonIcon,
  EuiCallOut,
  EuiFlexGroup,
  EuiFlexItem,
  EuiLoadingSpinner,
  EuiToolTip,
} from '@elastic/eui'

import { CuiCodeBlock } from '@modules/cui/CodeBlock'
import PrivacySensitiveContainer from '@modules/cui/PrivacySensitiveContainer'

type Props = {
  isLoading: boolean
  response: Response | undefined
}

const messages = defineMessages({
  rawOutput: {
    id: `api-console-output.raw-output`,
    defaultMessage: `Raw output`,
  },
})

const ApiConsoleOutput: React.FunctionComponent<Props> = ({ isLoading, response }) => {
  const { formatMessage } = useIntl()

  const [responseText, setResponseText] = useState<string | null>(null)

  useEffect(() => {
    if (response) {
      response.text().then(setResponseText)
    }
  }, [response])

  if (isLoading) {
    return (
      <EuiCallOut data-test-subj='apiConsoleOutputLoading' color='primary'>
        <EuiLoadingSpinner />
      </EuiCallOut>
    )
  }

  if (!response) {
    return null
  }

  const { headers, status, statusText } = response
  const contentType = headers.get('Content-Type')
  const language = contentType?.includes(`json`) ? 'json' : 'text'
  const output = language === 'json' ? indent(responseText) : responseText

  return (
    <Fragment>
      <EuiCallOut
        data-test-subj='apiConsoleOutputStatus'
        color={classifyStatus(status)}
        title={`${status} - ${statusText}`}
      />
      <PrivacySensitiveContainer>
        <CuiCodeBlock isVirtualized={true} overflowHeight={500} language={language}>
          {output}
        </CuiCodeBlock>
      </PrivacySensitiveContainer>
      {output && (
        <EuiFlexGroup justifyContent='flexEnd' gutterSize='s'>
          <EuiFlexItem grow={false}>
            <EuiToolTip
              position='top'
              content={formatMessage(messages.rawOutput)}
              disableScreenReaderOutput={true}
            >
              <EuiButtonIcon
                iconType='popout'
                href={getBlob(output, contentType)}
                aria-label={formatMessage(messages.rawOutput)}
                target='_blank'
              />
            </EuiToolTip>
          </EuiFlexItem>
        </EuiFlexGroup>
      )}
    </Fragment>
  )
}

function indent(json: string | null): string | null {
  if (json === null) {
    return json
  }

  try {
    return JSON.stringify(JSON.parse(json), null, 2)
  } catch (err) {
    return json // failed to indent, just use raw output
  }
}

function classifyStatus(status: number) {
  switch (Math.floor(status / 100)) {
    case 1:
      return `success`
    case 2:
      return `success`
    case 3:
      return `warning`
    default:
      return `danger`
  }
}

function getBlob(responseText: string, type: string | null): string {
  const blob: Blob = new window.Blob([responseText], {
    type: type ?? `application/json;charset=utf-8`,
  })

  const blobUrl: string = window.URL.createObjectURL(blob)

  return blobUrl
}

export default ApiConsoleOutput
